import { html } from '../utils/security.js';
import {
  getAppFactorySession,
  exportAppFactoryYaml,
  updateAppFactoryRequirements,
  importAppFactoryRequirements,
} from '../api.js';

export const renderAppFactoryReviewView = async (container, sessionId) => {
  let session = null;
  let requirements = null;

  container.innerHTML = html`
    <div class="container app-factory-review">
      <div class="page-header">
        <h1>Session Review & Export</h1>
        <div class="actions">
          <button type="button" id="back-btn" class="ghost-button">Zur\u00fcck</button>
          <button type="button" id="import-btn" class="ghost-button">Aus Conversation \u00fcbernehmen</button>
          <button type="button" id="export-yaml-btn" class="primary-button">YAML exportieren</button>
        </div>
      </div>

      <div id="review-content">
        <div class="loading-spinner">Lade Session-Daten...</div>
      </div>
    </div>
  `;

  loadSession();

  document.getElementById('back-btn')?.addEventListener('click', () => {
    window.location.hash = '#/app-factory';
  });

  document.getElementById('export-yaml-btn')?.addEventListener('click', exportYaml);

  document.getElementById('import-btn')?.addEventListener('click', async () => {
    const conversationId = window.prompt('Conversation ID zum Import eingeben');
    if (!conversationId) return;
    try {
      await importAppFactoryRequirements(sessionId, conversationId.trim());
      await loadSession();
      alert('Anforderungen wurden übernommen.');
    } catch (error) {
      console.error('Failed to import requirements', error);
      alert('Import fehlgeschlagen. Prüfe Conversation ID und Berechtigungen.');
    }
  });

  async function loadSession() {
    const reviewContent = document.getElementById('review-content');

    try {
      const response = await getAppFactorySession(sessionId);
      session = response?.session;
      requirements = response?.extractedRequirements ?? [];

      if (!session) {
        throw new Error('Session not found');
      }

      renderReviewContent();
    } catch (error) {
      console.error('Failed to load session:', error);
      reviewContent.innerHTML = html`
        <div class="error-state">
          <p class="error-message">Fehler beim Laden der Session: ${error.message}</p>
        </div>
      `;
    }
  }

  function renderReviewContent() {
    const reviewContent = document.getElementById('review-content');

    reviewContent.innerHTML = html`
      <div class="review-sections">
        <!-- Progress Summary -->
        <section class="review-section">
          <h2>Fortschritt</h2>
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${session.progress?.percentComplete ?? 0}%"></div>
          </div>
          <p class="muted">
            ${session.progress?.completedCategories ?? 0} / ${session.progress?.totalCategories ?? 0} Kategorien
            abgeschlossen (${session.progress?.percentComplete ?? 0}%)
          </p>
        </section>

        <!-- Requirements -->
        <section class="review-section">
          <h2>Anforderungen</h2>
          <div id="requirements-editor">
            ${requirements.length === 0 ? html`<p class="muted">Keine Anforderungen extrahiert.</p>` : ''}
            <div class="requirements-list">
              ${requirements
                .map(
                  (req, idx) => html`
                    <div class="requirement-item" data-index="${idx}">
                      <span class="requirement-type">${req.type ?? 'Anforderung'}</span>
                      <textarea
                        class="requirement-text"
                        data-index="${idx}"
                        rows="2"
                      >${req.text ?? req.description ?? ''}</textarea>
                      ${
                        req.priority
                          ? html`
                              <select class="requirement-priority" data-index="${idx}">
                                <option value="low" ${req.priority === 'low' ? 'selected' : ''}>Niedrig</option>
                                <option value="medium" ${req.priority === 'medium' ? 'selected' : ''}>
                                  Mittel
                                </option>
                                <option value="high" ${req.priority === 'high' ? 'selected' : ''}>Hoch</option>
                              </select>
                            `
                          : ''
                      }
                      <button type="button" class="remove-req-btn ghost-button small" data-index="${idx}">
                        Entfernen
                      </button>
                    </div>
                  `,
                )
                .join('')}
            </div>
            ${
              requirements.length > 0
                ? html`
                    <button type="button" id="save-requirements-btn" class="primary-button" style="margin-top: 1rem;">
                      \u00c4nderungen speichern
                    </button>
                  `
                : ''
            }
          </div>
        </section>

        <!-- YAML Preview -->
        <section class="review-section">
          <h2>YAML Export Vorschau</h2>
          <div id="yaml-preview">
            <p class="muted">Klicke auf "YAML exportieren" um die Vorschau zu sehen.</p>
          </div>
        </section>
      </div>
    `;

    // Attach event handlers
    document.querySelectorAll('.requirement-text').forEach((textarea) => {
      textarea.addEventListener('input', (e) => {
        const idx = parseInt(e.target.dataset.index);
        requirements[idx].text = e.target.value;
      });
    });

    document.querySelectorAll('.requirement-priority').forEach((select) => {
      select.addEventListener('change', (e) => {
        const idx = parseInt(e.target.dataset.index);
        requirements[idx].priority = e.target.value;
      });
    });

    document.querySelectorAll('.remove-req-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt(e.target.dataset.index);
        requirements.splice(idx, 1);
        renderReviewContent();
      });
    });

    document.getElementById('save-requirements-btn')?.addEventListener('click', saveRequirements);
  }

  async function saveRequirements() {
    const btn = document.getElementById('save-requirements-btn');
    btn.disabled = true;
    btn.textContent = 'Speichere...';

    try {
      await updateAppFactoryRequirements(sessionId, {
        functional: requirements.filter((r) => r.type?.includes('Funktional')),
        nonFunctional: requirements.filter((r) => !r.type?.includes('Funktional')),
        userStories: [],
      });

      alert('Anforderungen erfolgreich gespeichert!');
    } catch (error) {
      console.error('Failed to save requirements:', error);
      alert(`Fehler beim Speichern: ${error.message}`);
    } finally {
      btn.disabled = false;
      btn.textContent = '\u00c4nderungen speichern';
    }
  }

  async function exportYaml() {
    const btn = document.getElementById('export-yaml-btn');
    const yamlPreview = document.getElementById('yaml-preview');

    btn.disabled = true;
    btn.textContent = 'Exportiere...';

    try {
      const response = await exportAppFactoryYaml(sessionId);
      const yaml = response?.yaml;

      if (!yaml) {
        throw new Error('No YAML data returned');
      }

      // Display YAML preview
      yamlPreview.innerHTML = html`
        <pre class="yaml-content"><code>${yaml}</code></pre>
        <div class="yaml-actions">
          <button type="button" id="copy-yaml-btn" class="ghost-button">In Zwischenablage kopieren</button>
          <button type="button" id="download-yaml-btn" class="primary-button">Als Datei herunterladen</button>
        </div>
      `;

      document.getElementById('copy-yaml-btn')?.addEventListener('click', () => {
        navigator.clipboard.writeText(yaml);
        alert('YAML in Zwischenablage kopiert!');
      });

      document.getElementById('download-yaml-btn')?.addEventListener('click', () => {
        const blob = new Blob([yaml], { type: 'text/yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `app-factory-${sessionId.substring(0, 8)}.yaml`;
        a.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Failed to export YAML:', error);
      yamlPreview.innerHTML = html`
        <p class="error-message">Fehler beim Export: ${error.message}</p>
      `;
    } finally {
      btn.disabled = false;
      btn.textContent = 'YAML exportieren';
    }
  }
};
